import { useState, useEffect } from "react";
import { message } from "antd";
import API from "api/endpoints";
import axiosInstance from "api/axiosInstance";
import jsPDF from "jspdf";
import "jspdf-autotable";

function SkillReportService() {
  const [skills, setSkills] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [skillsLoading, setSkillsLoading] = useState(false);

  // Fetch departments on component mount
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const response = await axiosInstance.get(API.department.getList);
        setDepartments(response.data || []);
      } catch (error) {
        console.error("Error fetching departments:", error);
        message.error("Failed to load departments");
        setDepartments([]);
      }
    };
    fetchDepartments();
  }, []);

  // Fetch skills based on department selection
  useEffect(() => {
    const fetchSkills = async () => {
      try {
        setSkillsLoading(true);
        let url = API.skills.getList || "/Skills/GetList";

        if (selectedDepartment) {
          url += url.includes("?")
            ? `&DepartmentId=${selectedDepartment}`
            : `?DepartmentId=${selectedDepartment}`;
        }

        const response = await axiosInstance.get(url);
        setSkills(response.data || []);

        // Clear selected skills when department changes
        setSelectedSkills([]);
        setFilteredData([]);
      } catch (error) {
        console.error("Error fetching skills:", error);
        message.error("Failed to load skills");
        setSkills([]);
      } finally {
        setSkillsLoading(false);
      }
    };

    fetchSkills();
  }, [selectedDepartment]);

  // Handle department change
  const handleDepartmentChange = (departmentId) => {
    setSelectedDepartment(departmentId || "");
  };

  // Handle skills selection - API call for each selection
  const handleSkillsChange = async (selectedSkillIds) => {
    setSelectedSkills(selectedSkillIds || []);

    if (!selectedSkillIds || selectedSkillIds.length === 0) {
      setFilteredData([]);
      return;
    }

    try {
      setLoading(true);

      // Prepare request payload
      const payload = {
        SkillIds: selectedSkillIds,
        DepartmentId: selectedDepartment || null,
      };

      const response = await axiosInstance.post(API.reports.getEmployeesBySkills, payload);
      const employeeData = response.data || [];
      const transformedData = employeeData.map((employee) => ({
        id: employee.id,
        employeeId: employee.publicId,
        employeeName: employee.fullName,
        departmentName: employee.departmentName,
        skillsList: (employee.employeeSkills || []).map((s) => s.skillName).join(", "),
        jobTitleName: employee.jobTitleName || "N/A",
        hiringDate: employee.hiredDate,
      }));

      setFilteredData(transformedData);

      if (transformedData.length === 0) {
        message.info("No employees found with the selected skills");
      } else {
        message.success(`Found ${transformedData.length} employees with selected skills`);
      }
    } catch (error) {
      console.error("Error fetching employees by skills:", error);
      message.error("Failed to load employees data");
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get department name from ID
  const getDepartmentName = (departmentId) => {
    if (!departmentId) return "N/A";
    const department = departments.find((dept) => dept.id === departmentId);
    return department ? department.name : `Department ${departmentId}`;
  };

  // Download report as PDF
  const handleDownloadReport = () => {
    if (!filteredData.length) {
      message.warning("No data available to download");
      return;
    }

    try {
      const doc = new jsPDF("landscape");

      // Title
      doc.setFontSize(18);
      doc.setTextColor(40, 40, 40);
      doc.text("SKILLS REPORT", 148, 15, { align: "center" });

      // Report info
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);

      let currentY = 25;

      // Department info
      const departmentName = selectedDepartment
        ? getDepartmentName(parseInt(selectedDepartment))
        : "All Departments";
      doc.text(`Department: ${departmentName}`, 14, currentY);
      currentY += 6;

      // Selected skills info
      const selectedSkillNames = selectedSkills
        .map((skillId) => {
          const skill = skills.find((s) => s.id === skillId);
          return skill ? skill.name : `Skill ${skillId}`;
        })
        .join(", ");

      doc.text(`Selected Skills: ${selectedSkillNames}`, 14, currentY);
      currentY += 6;

      // Date and record count
      doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, currentY);
      currentY += 6;

      doc.text(`Total Records: ${filteredData.length}`, 14, currentY);
      currentY += 15;

      // Table headers - ONLY 6 requested columns
      const headers = [["Emp ID", "Name", "Department", "Skills List", "Job Title", "Hiring Date"]];

      // Table data - ONLY 6 requested columns
      const tableData = filteredData.map((employee) => [
        employee.employeeId?.toString() || "N/A",
        employee.employeeName || "N/A",
        employee.departmentName || "N/A",
        employee.skillsList || "No skills",
        employee.jobTitleName || "N/A",
        employee.hiringDate ? new Date(employee.hiringDate).toLocaleDateString() : "N/A",
      ]);

      // Generate table
      doc.autoTable({
        startY: currentY,
        head: headers,
        body: tableData,
        theme: "grid",
        styles: {
          fontSize: 8,
          cellPadding: 3,
        },
        headStyles: {
          fillColor: [70, 130, 180],
          textColor: 255,
          fontStyle: "bold",
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245],
        },
        columnStyles: {
          0: { cellWidth: 30 }, //  Emp ID (increased)
          1: { cellWidth: 26 }, // Name (slightly reduced)
          2: { cellWidth: 26 }, // Department (slightly reduced)
          3: { cellWidth: 90 }, // Skills (slightly reduced but still big)
          4: { cellWidth: 38 }, // Job Title (slightly reduced)
          5: { cellWidth: 30 }, // Hiring Date (same)
        },
        margin: { left: 10, right: 10 },
      });

      // Generate filename
      const timestamp = new Date().toISOString().split("T")[0];
      const filename = `Skills-Report-${departmentName.replace(/\s+/g, "-")}-${timestamp}.pdf`;

      // Save the PDF
      doc.save(filename);
      message.success("PDF report downloaded successfully!");
    } catch (error) {
      console.error("PDF generation error:", error);
      message.error("Failed to generate PDF report");
    }
  };

  // Table columns - ONLY 6 requested columns
  const columns = [
    {
      title: "Emp ID",
      dataIndex: "employeeId",
      key: "employeeId",
      width: 100,
    },
    {
      title: "Name",
      dataIndex: "employeeName",
      key: "employeeName",
      sorter: (a, b) => (a.employeeName || "").localeCompare(b.employeeName || ""),
    },
    {
      title: "Department",
      dataIndex: "departmentName",
      key: "departmentName",
      sorter: (a, b) => (a.departmentName || "").localeCompare(b.departmentName || ""),
    },
    {
      title: "Skills List",
      dataIndex: "skillsList",
      key: "skillsList",
    },
    {
      title: "Job Title",
      dataIndex: "jobTitleName",
      key: "jobTitleName",
      sorter: (a, b) => (a.jobTitleName || "").localeCompare(b.jobTitleName || ""),
    },
    {
      title: "Hiring Date",
      dataIndex: "hiringDate",
      key: "hiringDate",
      render: (date) => (date ? new Date(date).toLocaleDateString() : "N/A"),
      sorter: (a, b) => new Date(a.hiringDate || 0) - new Date(b.hiringDate || 0),
    },
  ];

  return {
    filteredData: filteredData || [],
    selectedSkills: selectedSkills || [],
    selectedDepartment: selectedDepartment || "",
    skills: skills || [],
    departments: departments || [],
    columns,
    loading: loading || skillsLoading,
    handleSkillsChange,
    handleDepartmentChange,
    handleDownloadReport,
  };
}

export default SkillReportService;
